import { useState, useEffect, useCallback } from 'react';
import { getInsuranceCompany } from '../api/insurance';
import type { InsuranceCompany } from '../types';

/**
 * Hook for fetching a single insurance company by ID
 */
export function useInsuranceCompany(id: number | undefined) {
  const [data, setData] = useState<InsuranceCompany | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompany = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const company = await getInsuranceCompany(id);
      setData(company);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch insurance company');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCompany,
  };
}
